import SalesOrderService from "@/services/api/salesOrder";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LoadingState {
  master: boolean;
  items: boolean;
  product: boolean;
  purchaseProduct: boolean;
  action: boolean;
  export: boolean;
  distributor: boolean;
}

interface SaleOrderState {
  saleOrderMasterList: any[];
  saleOrderItemsList: any[];
  saleOrderDetail: any[];
  productList: any[];
  purchaseProductList: any[];
  distributorList: any[];
  loading: LoadingState;
  error: string | null;
}

const initialState: SaleOrderState = {
  saleOrderMasterList: [],
  saleOrderItemsList: [],
  saleOrderDetail: [],
  productList: [],
  purchaseProductList: [],
  distributorList: [],
  loading: {
    master: false,
    items: false,
    product: false,
    purchaseProduct: false,
    action: false,
    export: false,
    distributor: false,
  },
  error: null,
};

export const getSaleOrderMasterList = createAsyncThunk(
  "saleOrder/getSaleOrderMasterList",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await SalesOrderService.getSaleOrderMasterList(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch sales order",
      );
    }
  },
);

export const getSaleOrderItemsList = createAsyncThunk(
  "saleOrder/getSaleOrderItemsList",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await SalesOrderService.getSaleOrderItemsList(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch sales order items",
      );
    }
  },
);

export const updateSaleOrderStatus = createAsyncThunk(
  "saleOrder/updateSaleOrderStatus",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await SalesOrderService.updateSaleOrderStatus(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update sales order status",
      );
    }
  },
);

export const addSaleOrder = createAsyncThunk(
  "saleOrder/addSaleOrder",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await SalesOrderService.addSaleOrder(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to add sales order",
      );
    }
  },
);

export const getProductList = createAsyncThunk(
  "saleOrder/getProductList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SalesOrderService.getProductList();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch product",
      );
    }
  },
);

export const updateDeliverStatus = createAsyncThunk(
  "saleOrder/updateDeliverStatus",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await SalesOrderService.updateDeliverStatus(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update deliver status",
      );
    }
  },
);

export const getPurchaseProductList = createAsyncThunk(
  "saleOrder/getPurchaseProductList",
  async (purchaseId: number, { rejectWithValue }) => {
    try {
      const response =
        await SalesOrderService.getPurchaseProductList(purchaseId);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch purchase product",
      );
    }
  },
);

export const getPurchaseExport = createAsyncThunk(
  "saleOrder/getPurchaseExport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await SalesOrderService.getPurchaseExport(data);
      return saveAsExcelFile(response, "Primary_SalesOrder");
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to export",
      );
    }
  },
);

export const getDistributorList = createAsyncThunk(
  "saleOrder/getDistributorList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SalesOrderService.getDistributorList();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch distributors",
      );
    }
  },
);

const saleOrderSlice = createSlice({
  name: "saleOrder",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSaleOrderMasterList.pending, (state) => {
        state.loading.master = true;
        state.error = null;
        state.saleOrderMasterList = [];
      })
      .addCase(getSaleOrderMasterList.fulfilled, (state, action) => {
        state.loading.master = false;
        state.saleOrderMasterList = action.payload || [];
      })
      .addCase(getSaleOrderMasterList.rejected, (state, action) => {
        state.loading.master = false;
        state.error = action.payload as string;
      })

      .addCase(getSaleOrderItemsList.pending, (state) => {
        state.loading.items = true;
        state.error = null;
        state.saleOrderItemsList = [];
      })
      .addCase(getSaleOrderItemsList.fulfilled, (state, action) => {
        state.loading.items = false;
        state.saleOrderItemsList = action.payload || [];
      })
      .addCase(getSaleOrderItemsList.rejected, (state, action) => {
        state.loading.items = false;
        state.error = action.payload as string;
      })

      .addCase(getProductList.pending, (state) => {
        state.loading.product = true;
        state.error = null;
        state.productList = [];
      })
      .addCase(getProductList.fulfilled, (state, action) => {
        state.loading.product = false;
        state.productList = action.payload || [];
      })
      .addCase(getProductList.rejected, (state, action) => {
        state.loading.purchaseProduct = false;
        state.error = action.payload as string;
      })

      .addCase(getPurchaseProductList.pending, (state) => {
        state.loading.purchaseProduct = true;
        state.error = null;
        state.purchaseProductList = [];
      })
      .addCase(getPurchaseProductList.fulfilled, (state, action) => {
        state.loading.purchaseProduct = false;
        state.purchaseProductList = action.payload || [];
      })
      .addCase(getPurchaseProductList.rejected, (state, action) => {
        state.loading.purchaseProduct = false;
        state.error = action.payload as string;
      })

      .addCase(addSaleOrder.pending, (state) => {
        state.loading.action = true;
        state.error = null;
      })
      .addCase(addSaleOrder.fulfilled, (state) => {
        state.loading.action = false;
      })
      .addCase(addSaleOrder.rejected, (state, action) => {
        state.loading.action = false;
        state.error = action.payload as string;
      })

      .addCase(updateSaleOrderStatus.pending, (state) => {
        state.loading.action = true;
        state.error = null;
      })
      .addCase(updateSaleOrderStatus.fulfilled, (state) => {
        state.loading.action = false;
      })
      .addCase(updateSaleOrderStatus.rejected, (state, action) => {
        state.loading.action = false;
        state.error = action.payload as string;
      })

      .addCase(updateDeliverStatus.pending, (state) => {
        state.loading.action = true;
        state.error = null;
      })
      .addCase(updateDeliverStatus.fulfilled, (state) => {
        state.loading.action = false;
      })
      .addCase(updateDeliverStatus.rejected, (state, action) => {
        state.loading.action = false;
        state.error = action.payload as string;
      })

      .addCase(getPurchaseExport.pending, (state) => {
        state.loading.export = true;
        state.error = null;
      })
      .addCase(getPurchaseExport.fulfilled, (state) => {
        state.loading.export = false;
      })
      .addCase(getPurchaseExport.rejected, (state, action) => {
        state.loading.export = false;
        state.error = action.payload as string;
      })

      .addCase(getDistributorList.pending, (state) => {
        state.loading.distributor = true;
        state.error = null;
        state.distributorList = [];
      })
      .addCase(getDistributorList.fulfilled, (state, action) => {
        state.loading.distributor = false;
        state.distributorList = action.payload || [];
      })
      .addCase(getDistributorList.rejected, (state, action) => {
        state.loading.distributor = false;
        state.error = action.payload as string;
      });
  },
});

export default saleOrderSlice.reducer;
